import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { BulletList } from "@tiptap/extension-list";
import { useEffect } from "react";
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered,
  Heading2, Heading3, Pilcrow, Undo2, Redo2, Eraser, Link2, Link2Off,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Check, X } from "lucide-react";

// BulletList that can carry an approved variant class (rte-check / rte-cross) for ✓/✕ markers.
const VariantBulletList = BulletList.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (el) => el.getAttribute("class"),
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
      },
    };
  },
});

const Btn = ({ active, onClick, title, disabled, children, testid }) => (
  <button type="button" title={title} data-testid={testid} disabled={disabled}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`flex h-8 w-8 items-center justify-center rounded-md text-charcoal transition-colors disabled:opacity-40 ${active ? "bg-saffron text-white" : "hover:bg-cream"}`}>
    {children}
  </button>
);

export function RichTextEditor({ value, onChange, placeholder = "Start typing…", minHeight = 120, testid }) {
  const editor = useEditor({
    immediatelyRender: true,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: false,
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } },
      }),
      VariantBulletList,
      TextAlign.configure({ types: ["heading", "paragraph"], alignments: ["left", "center", "right", "justify"] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: { attributes: { class: "ms-rte-content focus:outline-none" } },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming !== current && !(incoming === "" && current === "<p></p>")) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;
  const tid = (s) => (testid ? `${testid}-${s}` : s);

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove)", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  // Toggle a bullet list and stamp it with an approved variant class (plain/check/cross).
  const listVariant = (variant) => {
    const chain = editor.chain().focus();
    if (!editor.isActive("bulletList")) chain.toggleBulletList();
    chain.updateAttributes("bulletList", { class: variant }).run();
  };
  const bulletClass = editor.getAttributes("bulletList").class || null;
  const isVariant = (v) => editor.isActive("bulletList") && bulletClass === v;

  return (
    <div className="rounded-lg border border-input bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1">
        <Btn testid={tid("bold")} title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn testid={tid("italic")} title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn testid={tid("underline")} title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Btn>
        <Btn testid={tid("strike")} title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={tid("bullet")} title="Bulleted list" active={isVariant(null)} onClick={() => listVariant(null)}><List className="h-4 w-4" /></Btn>
        <Btn testid={tid("ordered")} title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn testid={tid("checklist")} title="Checkmark list" active={isVariant("rte-check")} onClick={() => listVariant("rte-check")}><Check className="h-4 w-4" /></Btn>
        <Btn testid={tid("crosslist")} title="Cross list" active={isVariant("rte-cross")} onClick={() => listVariant("rte-cross")}><X className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={tid("align-left")} title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></Btn>
        <Btn testid={tid("align-center")} title="Align centre" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></Btn>
        <Btn testid={tid("align-right")} title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></Btn>
        <Btn testid={tid("align-justify")} title="Justify" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={tid("paragraph")} title="Paragraph" active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}><Pilcrow className="h-4 w-4" /></Btn>
        <Btn testid={tid("h2")} title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Btn>
        <Btn testid={tid("h3")} title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={tid("link")} title="Add/edit link" active={editor.isActive("link")} onClick={setLink}><Link2 className="h-4 w-4" /></Btn>
        <Btn testid={tid("unlink")} title="Remove link" disabled={!editor.isActive("link")} onClick={() => editor.chain().focus().unsetLink().run()}><Link2Off className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={tid("undo")} title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></Btn>
        <Btn testid={tid("redo")} title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></Btn>
        <Btn testid={tid("clear")} title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><Eraser className="h-4 w-4" /></Btn>
      </div>
      <EditorContent editor={editor} style={{ minHeight }} className="ms-rte px-3 py-2 text-sm text-charcoal" />
    </div>
  );
}
